'use client';

import Script from 'next/script';

import { useCspNonce } from '@/components/security/CspNonceProvider';
import { sanitizeJsonLdString } from '@/components/seo/json-ld-sanitize';

/**
 * Safe JSON-LD Structured Data Component
 *
 * SECURITY NOTE: This component uses dangerouslySetInnerHTML for JSON-LD structured data.
 * This is SAFE because:
 * - The data comes from generateStructuredData() or buildServiceJsonLd() which only use static, trusted data
 * - We only accept object/array (never raw strings that could contain HTML)
 * - JSON.stringify() escapes quotes and backslashes; we additionally escape </script> to prevent script breakout
 * - The content is never user-generated or from external sources without sanitization
 *
 * This component isolates the dangerous pattern to a single, well-documented location.
 *
 * Prefer {@link JsonLdScript} from Server Components (layouts/pages) to avoid React 19
 * devtools warnings about `<script>` in client trees.
 */

interface SafeJsonLdProps {
  /**
   * Structured data object (or array for @graph) to serialize as JSON-LD.
   * Must be trusted, static data - never pass raw user input or unvalidated external content.
   */
  data: Record<string, unknown> | Array<unknown>;
  /**
   * Unique ID for the script tag
   */
  id?: string;
  /**
   * Use regular script tag instead of Next.js Script component
   * Useful for server components that need suppressHydrationWarning
   */
  useRegularScript?: boolean;
  /**
   * CSP nonce (usually from `CspNonceProvider` / edge proxy). Override for tests.
   */
  nonce?: string;
}

export function SafeJsonLd({
  data,
  id = 'structured-data',
  useRegularScript = false,
  nonce: nonceProp,
}: SafeJsonLdProps) {
  const nonceFromContext = useCspNonce();
  const nonce = nonceProp ?? nonceFromContext ?? '';

  // Reject non-serializable or risky input (never accept pre-stringified HTML)
  const isAcceptable =
    typeof data === 'object' &&
    data !== null &&
    (Array.isArray(data) || Object.prototype.toString.call(data) === '[object Object]');
  if (!isAcceptable) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('SafeJsonLd: data must be a plain object or array; received', typeof data);
    }
    return null;
  }

  const jsonLd = sanitizeJsonLdString(JSON.stringify(data));

  if (useRegularScript) {
    return (
      <script
        id={id}
        type='application/ld+json'
        nonce={nonce || undefined}
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
    );
  }

  return (
    <Script
      id={id}
      type='application/ld+json'
      nonce={nonce || undefined}
      dangerouslySetInnerHTML={{ __html: jsonLd }}
    />
  );
}
